import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Radio, Eye, MousePointerClick, MessageSquareQuote, PlayCircle, UserCheck, Percent } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader } from "./AdminLayout";

const Card = ({ icon: Icon, label, value, hint, testid }) => (
    <div className="rounded-2xl bg-white border border-gold/25 p-5 shadow-sm" data-testid={testid}>
        <div className="flex items-center justify-between">
            <p className="text-xs font-mono uppercase tracking-widest text-pineneedle">{label}</p>
            <Icon size={17} className="text-gold" />
        </div>
        <p className="font-heading font-extrabold text-3xl text-forest mt-2">{value}</p>
        {hint && <p className="text-[11px] text-pineneedle/70 mt-1 leading-snug">{hint}</p>}
    </div>
);

export default function Dashboard() {
    const [d, setD] = useState(null);

    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
    }, []);

    if (d === false) return <p className="text-destructive">Impossible de charger le tableau de bord.</p>;
    if (!d) return <p className="text-pineneedle">Chargement…</p>;

    return (
        <div data-testid="admin-dashboard">
            <PageHeader title="Tableau de bord" subtitle="Toutes les métriques proviennent des données réelles du site. Zéro signifie : aucune donnée mesurée." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card icon={Radio} label="Sessions actives" value={d.active_sessions} hint={d.active_session_definition} testid="kpi-sessions-actives" />
                <Card icon={UserCheck} label="Équipe connectée" value={d.staff_online} hint="Sessions actives de comptes d'administration" testid="kpi-staff-online" />
                <Card icon={Users} label="Visiteurs uniques (30 j)" value={d.visitors_unique_30d} hint={d.visitor_method} testid="kpi-visitors-30d" />
                <Card icon={Eye} label="Pages vues (30 j)" value={d.page_views_30d} hint={`${d.page_views_24h} sur les dernières 24 h`} testid="kpi-pageviews-30d" />
                <Card icon={Users} label="Comptes du site" value={d.accounts} hint="Comptes enregistrés sur le site web uniquement" testid="kpi-accounts" />
                <Card icon={MessageSquareQuote} label="Témoignages" value={`${d.testimonials_pending} / ${d.testimonials_published}`} hint="En attente / publiés" testid="kpi-testimonials" />
                <Card icon={MousePointerClick} label="Clics stores" value={`${d.clicks_android} / ${d.clicks_ios}`} hint="Android / iOS, cumul réel" testid="kpi-store-clicks" />
                <Card icon={PlayCircle} label="Lectures vidéo" value={d.video_plays} hint="Lectures enregistrées des vidéos publiées" testid="kpi-video-plays" />
            </div>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-2xl bg-white border border-gold/25 p-6 shadow-sm" data-testid="views-chart-card">
                    <h2 className="font-heading font-bold text-forest mb-4">Pages vues — 14 derniers jours</h2>
                    {d.views_chart.length === 0 ? (
                        <p className="text-sm text-pineneedle py-10 text-center">Aucune visite mesurée pour le moment. Les statistiques apparaissent dès que des visiteurs consentants naviguent sur le site.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={d.views_chart}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e0d2" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                                <Tooltip formatter={(v) => [`${v} vue(s)`, "Pages"]} labelFormatter={(v) => new Date(v).toLocaleDateString("fr-FR")} />
                                <Area type="monotone" dataKey="views" stroke="#0B2B1E" fill="#C5A059" fillOpacity={0.35} strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
                <div className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm" data-testid="promo-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Percent size={17} className="text-gold" />
                        <h2 className="font-heading font-bold text-forest">Promotions</h2>
                    </div>
                    <p className="font-heading font-extrabold text-4xl text-forest">{d.promos_live}</p>
                    <p className="text-sm text-pineneedle mt-1">promotion(s) actuellement en cours de validité</p>
                    <p className="text-[11px] text-pineneedle/70 mt-4 leading-snug">
                        Rappel : une promotion publiée ici n'active aucune remise dans les stores. Elle doit refléter une offre réellement applicable sur Google Play ou l'App Store.
                    </p>
                </div>
            </div>
        </div>
    );
}
